// A small typed event emitter with the method names LiveKit's objects expose
// (on/off/once/emit/removeAllListeners), so app code that subscribes to
// Room/Participant/Track events keeps working.

// Method-shorthand trick: parameters are checked bivariantly, so handlers typed
// `(...args: Array<never>) => void` (as Fluxer's debug sink uses) are accepted
// while untyped arrow handlers still get `any` parameters.
type Listener = {bivarianceHack(...args: Array<any>): void}['bivarianceHack'];

export class Emitter<Events extends string = string> {
	#listeners = new Map<string, Set<Listener>>();

	on(event: Events, listener: Listener): this {
		let set = this.#listeners.get(event);
		if (!set) {
			set = new Set();
			this.#listeners.set(event, set);
		}
		set.add(listener);
		return this;
	}

	addListener(event: Events, listener: Listener): this {
		return this.on(event, listener);
	}

	once(event: Events, listener: Listener): this {
		const wrapped: Listener = (...args) => {
			this.off(event, wrapped);
			listener(...args);
		};
		return this.on(event, wrapped);
	}

	off(event: Events, listener: Listener): this {
		this.#listeners.get(event)?.delete(listener);
		return this;
	}

	removeListener(event: Events, listener: Listener): this {
		return this.off(event, listener);
	}

	removeAllListeners(event?: Events): this {
		if (event === undefined) this.#listeners.clear();
		else this.#listeners.delete(event);
		return this;
	}

	listenerCount(event: Events): number {
		return this.#listeners.get(event)?.size ?? 0;
	}

	emit(event: Events, ...args: Array<unknown>): boolean {
		const set = this.#listeners.get(event);
		if (!set || set.size === 0) return false;
		for (const listener of [...set]) {
			try {
				listener(...args);
			} catch (error) {
				console.error(`[parley/rtc] listener for "${event}" threw`, error);
			}
		}
		return true;
	}
}
